import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid2,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { 
  CreditCard, 
  AccountBalanceWallet,
  Close as CloseIcon,
  Send as SendIcon,
  CardGiftcard as GiftIcon
} from "@mui/icons-material";
import { FaPaypal } from "react-icons/fa";

import { LottieAnimation } from "../lottie/LottieAnimation.js";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import BuyCoinsSlider from "./BuyCoinsSlider";
import { useTranslation } from "react-i18next";
import ChessButton from "./ChessButton";
import Swal from "sweetalert2";
import { GENERAL } from "../general/General";
import { styled } from "@mui/system";
import { FaCrown } from "react-icons/fa";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import PayPalPurchase from "./PayPalPurchase.js";
import { FaChessRook } from "react-icons/fa6";
import { FaChessPawn } from "react-icons/fa";
import SupportButton from "./SupportButton.js";
import DropIn from "./DropIn";
import SubscriptionDropIn from "./SubscriptionDropIn";
import SendCoins from "./SendCoins"; // Import your existing SendCoins component

const AIRWALLEX_ENV = "demo"; // change to 'prod' when ready

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ theme, isSelected }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  transition: "all 0.3s ease-in-out",
  backgroundColor: isSelected ? "moccasin" : "#fff",
  cursor: "pointer",
  transform: isSelected ? "scale(1.06)" : "scale(1)",
  border: isSelected ? `3px solid #4a707d` : "2px solid transparent",
  borderRadius: "15px",
  boxShadow: isSelected
    ? `0 0 20px ${theme.palette.primary?.light}`
    : `0 2px 8px rgba(0,0,0,0.1)`,
  position: "relative",
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: "bold",
  marginBottom: theme.spacing(2),
}));

const PaymentMethodToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  borderRadius: "12px",
  padding: "8px",
  marginBottom: "20px",
  "& .MuiToggleButton-root": {
    border: "2px solid transparent",
    borderRadius: "8px",
    padding: "12px 20px",
    margin: "0 4px",
    fontWeight: "bold",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(74, 112, 125, 0.1)",
    },
    "&.Mui-selected": {
      backgroundColor: "#4a707d",
      color: "white",
      border: "2px solid #4a707d",
      "&:hover": {
        backgroundColor: "#3a5a65",
      },
    },
  },
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "20px",
  padding: "12px 24px",
  margin: "0 8px",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "1rem",
  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
  },
}));

const TransitionSlide = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PurchaseCoinsAirWallex = ({
  onClose,
  scrollToCoins = false,
  userData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [coinPackages, setCoinPackages] = useState([]);
  const [clientToken, setClientToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubscriptionPlanId, setSelectedSubscriptionPlanId] = useState(null);
  const [selectedPackagePlanId, setSelectedPackagePlanId] = useState(null);
  const coinPackagesRef = useRef(null);
  const [, setSelectedPrice] = useState(0.0);
  const paymentSectionRef = useRef(null);
  const [language, setLanguage] = useState("en");
  const gridBackGraundPackage = "black";
  const [subscriptionPrices, setSubscriptionPrices] = useState(0.0);
  const [packagePrice, setPackagePrice] = useState(0.0);
  const subscriptionButtonRef = useRef(null);
  const [, setClientSecret] = useState(null);
  const [userTopSubscriptionId, setUserTopSubscriptionId] = useState(
    userData?.topSubscription?.planId ?? null
  );
  const [userTopSubscriptionPackage, setUserTopSubscriptionPackage] = useState(
    userData?.topSubscription ?? null
  );
  
  // Payment method selection state
  const [paymentMethod, setPaymentMethod] = useState(null);
  
  // Separate state for different message types
  const [subscriptionMessage, setSubscriptionMessage] = useState(null);
  const [packageMessage, setPackageMessage] = useState(null);
  
  // DropIn integration states
  const [showSubscriptionDropIn, setShowSubscriptionDropIn] = useState(false);
  const [showPackageDropIn, setShowPackageDropIn] = useState(false);
  const [subscriptionAmount, setSubscriptionAmount] = useState(0);
  const [packageAmount, setPackageAmount] = useState(0);

  // NEW: States for quick actions
  const [showSendCoins, setShowSendCoins] = useState(false);

  useEffect(() => {
    if (scrollToCoins) {
      const scrollTimeout = setTimeout(() => {
        if (coinPackagesRef.current) {
          coinPackagesRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 500);

      return () => clearTimeout(scrollTimeout);
    }
  }, [scrollToCoins]);

  useEffect(() => {
    if (selectedSubscriptionPlanId && subscriptionPrices > 0 && subscriptionButtonRef.current) {
      setTimeout(() => {
        subscriptionButtonRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [selectedSubscriptionPlanId, subscriptionPrices]);

  useEffect(() => {
    if (selectedPackagePlanId && packagePrice > 0 && paymentSectionRef.current) {
      setTimeout(() => {
        paymentSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [selectedPackagePlanId, packagePrice]);

  useEffect(() => {
    const handleWebSocketEvent = (event) => {
      if (event.type === "message") {
        const messageType = event.message.messageType;
        
        const subscriptionMessages = [
          "subscription_setup_complete",
          "subscription_setup_failed",
          "subscription_created_success",
          "subscription_creation_failed",
          "subscription_payment_consent_created",
          "subscription_created",
          "subscription_creation_error"
        ];
        
        if (subscriptionMessages.includes(messageType)) {
          setSubscriptionMessage({
            ...event.message,
            timestamp: Date.now()
          });
          return;
        }
        
        const packageMessages = [
          "airwallex_client_secret",
          "payment_intent_created", 
          "payment_failed",
          "payment_success"
        ];
        
        if (packageMessages.includes(messageType)) {
          setPackageMessage({
            ...event.message,
            timestamp: Date.now()
          });
          return;
        }
        
        switch (messageType) {
          case "send_token_bt":
            setClientToken(event.message.payment.token);
            setLoading(false);
            break;
            
          case "purchase_ok_bt":
          case "purchase_ok_pp":
            setLoading(false);
            setUserTopSubscriptionId(
              event.message.user?.topSubscription?.planId ?? null
            );
            Swal.fire({
              title: t("Congratulation"),
              icon: "success",
              confirmButtonText: t("OK"),
            }).then(() => {
              onClose();
            });
            break;
            
          case "purchase_fail_bt":
            setLoading(false);
            Swal.fire({
              title: t("Payment failed. Please try again..."),
              icon: "error",
              draggable: true,
              confirmButtonText: t("OK"),
            });
            break;
            
          case "coin_packages":
            setCoinPackages(event.message.coinsPackages || []);
            setLoading(false);
            sessionStorage.setItem("coinPackagesLoaded", "true");
            setUserTopSubscriptionId(
              event.message.user?.topSubscription?.planId ?? null
            );
            setUserTopSubscriptionPackage(event.message.user?.topSubscription);
            break;
            
          case "user_data":
            break;
            
          case "already_rewarded":
            Swal.fire({
              title: t(
                "You've already claimed today's reward. Come back tomorrow for more!"
              ),
              icon: "warning",
              draggable: true,
              confirmButtonText: t("OK"),
            });
            break;
            
          case "ping":
            break;
            
          default:
            break;
        }
      }
    };

    addListener(handleWebSocketEvent);

    let generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "coin_packages";
    generalMessage.game = null;
    generalMessage.gameInfo = null;
    sendMessage(generalMessage);

    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, []);

  useEffect(() => {
    const persistedLanguage = localStorage.getItem("selectedLanguage");
    if (persistedLanguage) {
      const lang = JSON.parse(persistedLanguage);
      setLanguage(lang.code);
    }
    if (userData) {
      if (!userData.email && !userData.socialUser) {
        Swal.fire({
          title: t("Please log in to continue with your coin purchase"),
          icon: "warning",
          draggable: true,
          confirmButtonText: t("OK"),
        });
        onClose();
      }
    } else {
      Swal.fire({
        title: t("Please log in to continue with your coin purchase"),
        icon: "warning",
        draggable: true,
        confirmButtonText: t("OK"),
      });
      onClose();
    }
  }, [userData, t, onClose]);

  useEffect(() => {
    let selectedPrice = subscriptionPrices + packagePrice;
    setSelectedPrice(selectedPrice);
  }, [subscriptionPrices, packagePrice]);

  const handlePaymentMethodChange = (event, newPaymentMethod) => {
    if (newPaymentMethod !== null) {
      setPaymentMethod(newPaymentMethod);
    }
  };

  const handleSubscriptionSelection = (planId, packageType, price, pkg) => {
    if (!pkg) {
      return;
    }
    if (planId === userTopSubscriptionId) {
      Swal.fire({
        title: t("You already own this plan"),
        icon: "info",
        confirmButtonText: t("OK"),
      });
      return;
    }
    if (pkg?.level <= 100) {
      Swal.fire({
        title: t("Free plan"),
        icon: "info",
        confirmButtonText: t("OK"),
      });
      return;
    }
    if (pkg?.level <= userTopSubscriptionPackage?.level) {
      Swal.fire({
        title: t("You already have a better plan than the one you selected"),
        icon: "info",
        confirmButtonText: t("OK"),
      });
      return;
    }
    
    if (selectedSubscriptionPlanId !== planId) {
      setPaymentMethod(null);
    }
    
    setShowPackageDropIn(false);
    setShowSubscriptionDropIn(false);

    setSelectedSubscriptionPlanId(planId);
    setSubscriptionPrices(parseFloat(price));
    setSubscriptionAmount(parseFloat(price));
    
    setSelectedPackagePlanId(null);
    setPackagePrice(0);
  };

  const handlePackageSelection = (planId, packageType, price, pkg) => {
    if (!pkg) {
      return;
    }

    if(pkg.level === 10){
      return;
    }
    
    if (selectedPackagePlanId !== planId) {
      setPaymentMethod(null);
    }
    
    setShowPackageDropIn(false);
    setShowSubscriptionDropIn(false);
    
    setSelectedPackagePlanId(planId);
    setPackagePrice(parseFloat(price));
    setPackageAmount(parseFloat(price));

    setSelectedSubscriptionPlanId(null);
    setSubscriptionPrices(0);
  };

  const handleCheckDailyRewards = () => {
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "reward";
    sendMessage(generalMessage);
  };

  const handleSubscriptionSuccess = (paymentConsent) => {
    Swal.fire({
      title: t("Subscription Setup Complete!"),
      text: t("Your subscription is being activated..."),
      icon: "success",
      confirmButtonText: t("OK"),
    });
  };

  const handlePaymentSuccess = (paymentDetails) => {
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "airwallex_payment_success";
    
    generalMessage.payment = generalMessage.payment || {};
    generalMessage.payment.paymentDetails = JSON.stringify(paymentDetails);
    generalMessage.payment.intentId = paymentDetails.intent.id;
    generalMessage.payment.planId = selectedPackagePlanId;
    generalMessage.payment.planType = "package";
    generalMessage.payment.amount = packageAmount;
    
    sendMessage(generalMessage);
    
    Swal.fire({
      title: t("Payment Successful!"),
      text: t("Your purchase has been completed successfully."),
      icon: "success",
      confirmButtonText: t("OK"),
    }).then(() => {
      onClose();
    });
  };

  const handlePaymentError = (error) => {
    console.error("Payment error:", error);
    Swal.fire({
      title: t("Payment Failed"),
      text: t("There was an issue processing your payment. Please try again."),
      icon: "error",
      confirmButtonText: t("OK"),
    });
  };

  const resolveFeatureAvailability = (pkg, key) => {
    if (!pkg || !key) return false;

    switch (key) {
      case "feature.viewerAccess":
        return pkg.viewerAccess;
      case "feature.adFree":
        return pkg.adFree;
      case "feature.analyticsDashboard":
        return pkg.analyticsDashboard;
      case "feature.priorityMatchmaking":
        return pkg.priorityMatchmaking;
      case "feature.earlyAccess":
        return pkg.earlyAccess;
      case "feature.teamCollaboration":
        return pkg.teamCollaboration;
      case "feature.unlimitedSavedGames":
        return pkg.unlimitedSavedGames;
      case "feature.unlimitedTeams":
        return pkg.unlimitedTeams;
      case "feature.coinsPerDay":
        return pkg.coinsPerDay > 0;
      case "feature.coinsPerMonth":
        return pkg.coinsPerMonth > 0;
      case "feature.maxGames":
        return pkg.maxGames > 0 || pkg.maxGames === -1;
      case "feature.maxTeams":
        return pkg.maxTeams > 0 || pkg.maxTeams === -1;
      case "feature.hintsPerGame":
        return pkg.hintsPerGame > 0;
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LottieAnimation
          path={
            "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743417798/Lottie/heart.json"
          }
          width="70px"
          autoplay
          loop
        />
      </Box>
    );
  }

  const subscriptionPlans = coinPackages.filter(
    (pkg) => pkg.packageType === "subscription"
  );
  const recommendedPlanId =
    subscriptionPlans[Math.floor(subscriptionPlans.length / 2)]?.planId;

  return (
    <Box sx={{ marginTop: "20px", textAlign: "center" }}>
      {/* ENHANCED: Quick Actions Section */}
      <Box 
        sx={{ 
          margin: "20px 0",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "20px",
          padding: "20px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            color: "white", 
            fontWeight: "bold", 
            marginBottom: "20px",
            textShadow: "0 2px 4px rgba(0,0,0,0.3)"
          }}
        >
          {t("Quick Actions")}
        </Typography>
        
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          gap: 2,
          flexWrap: "wrap"
        }}>
          <QuickActionButton
            variant="contained"
            startIcon={<GiftIcon />}
            onClick={handleCheckDailyRewards}
            sx={{
              background: "linear-gradient(45deg, #4CAF50 30%, #45a049 90%)",
              color: "white",
              minWidth: isMobile ? "140px" : "180px",
            }}
          >
            {t("Check Daily Rewards")}
          </QuickActionButton>
          
          <QuickActionButton
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setShowSendCoins(true)}
            sx={{
              background: "linear-gradient(45deg, #FF6B6B 30%, #ee5a52 90%)",
              color: "white",
              minWidth: isMobile ? "140px" : "180px",
            }}
          >
            {t("Transfer Coins")}
          </QuickActionButton>
        </Box>
      </Box>

      {/* SendCoins Dialog */}
      <Dialog
        open={showSendCoins}
        onClose={() => setShowSendCoins(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={TransitionSlide}
        sx={{ zIndex: 9999 }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "20px",
            overflow: "hidden",
            zIndex: 9999,
          }
        }}
        BackdropProps={{
          sx: {
            zIndex: 9998,
          }
        }}
      >
        <Box sx={{ position: "relative" }}>
          {!isMobile && (
            <IconButton
              onClick={() => setShowSendCoins(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                zIndex: 1,
                backgroundColor: "rgba(255,255,255,0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <DialogContent sx={{ padding: 0, position: 'relative', zIndex: 9999 }}>
            <Box sx={{ position: 'relative', zIndex: 9999 }}>
              <SendCoins />
            </Box>
          </DialogContent>
        </Box>
      </Dialog>

      {/* Subscription Plans Section */}
      <Box
        sx={{
          backgroundColor: gridBackGraundPackage,
          border: "1px solid #ccc",
          padding: "8px",
          borderRadius: "10px",
          position: "relative",
          backgroundImage: GENERAL.BACKGROUND,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Typography
            variant="h4"
            sx={{
              marginTop: "10px",
              fontWeight: "bold",
              color: "white",
              marginBottom: "20px",
            }}
          >
            {t("Select your Plan")}
          </Typography>

          <Grid2
            container
            spacing={4}
            alignItems="stretch"
            sx={{
              mb: 5,
              minHeight: "30vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {coinPackages
              .filter((pkg) => pkg.packageType === "subscription")
              .map((pkg) => (
                <Grid2
                  item
                  xs={12}
                  md={4}
                  key={pkg.planId}
                  display="flex"
                  justifyContent="center"
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <StyledCard
                    isSelected={selectedSubscriptionPlanId === pkg.planId}
                    onClick={() => {
                      handleSubscriptionSelection(
                        pkg.planId,
                        "subscription",
                        pkg.price,
                        pkg
                      );
                    }}
                    sx={{
                      pointerEvents: "auto",
                      opacity: 1,
                    }}
                  >
                    <CardContent
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1.25}
                      >
                        <Typography
                          variant="h5"
                          component="h2"
                          sx={{
                            fontWeight: "bold",
                            color: "#4a707d",
                          }}
                        >
                          {pkg?.name}
                        </Typography>
                        {pkg?.planId === "basic" && (
                          <Chip
                            icon={
                              <FaChessPawn
                                style={{ marginRight: 0, color: "white" }}
                              />
                            }
                            label={t("For free")}
                            sx={{
                              backgroundColor: "green",
                              fontSize: isMobile ? "9px" : "15px",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                        {pkg?.planId === recommendedPlanId && (
                          <Chip
                            icon={
                              <FaChessRook
                                style={{ marginRight: 0, color: "white" }}
                              />
                            }
                            label={t("RECOMMENDED")}
                            sx={{
                              backgroundColor: "#4a707d",
                              fontSize: isMobile ? "9px" : "15px",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                        {pkg?.level >= 300 && (
                          <Chip
                            icon={
                              <FaCrown
                                style={{ marginRight: 0, color: "white" }}
                              />
                            }
                            label={t("BEST")}
                            sx={{
                              backgroundColor: "black",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                      </Box>
                      <PriceText
                        color="primary"
                        sx={{ display: "flex", alignItems: "baseline" }}
                      >
                        ${Number(pkg?.price).toFixed(2)}
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.8rem", ml: 0.5 }}
                        >
                          /{t("Month")}
                        </Typography>
                      </PriceText>

                      {pkg.featureKeys?.map((key, index) => {
                        const isAvailable = resolveFeatureAvailability(
                          pkg,
                          key
                        );
                        const icon = isAvailable ? (
                          <FaCheckCircle color="#2e7d32" size="1.2em" />
                        ) : (
                          <FaTimesCircle color="#d32f2f" size="1.2em" />
                        );

                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              textAlign: "left",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Box sx={{ pt: "0px" }}>{icon}</Box>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {t(key, {
                                coinsPerDay: pkg.coinsPerDay,
                                coinsPerMonth: pkg.coinsPerMonth,
                                maxGames: pkg.maxGames,
                                maxTeams: pkg.maxTeams,
                                hintsPerGame: pkg.hintsPerGame,
                              })}
                            </Typography>
                          </Box>
                        );
                      })}
                    </CardContent>

                    {pkg.planId === userTopSubscriptionId && (
                      <Box>
                        <Chip
                          label={t("Active Plan").toUpperCase()}
                          size="small"
                          sx={{
                            marginBottom: "18px",
                            backgroundColor: "#fdf2e9",
                            color: "#a0522d",
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            border: "2px dashed #a0522d",
                            borderRadius: "6px",
                            textTransform: "uppercase",
                            boxShadow: "0 0 6px rgba(0, 0, 0, 0.2)",
                            letterSpacing: "1px",
                            paddingX: "6px",
                            transform: "rotate(-19deg)",
                            display: "inline-block",
                          }}
                        />
                      </Box>
                    )}
                  </StyledCard>
                </Grid2>
              ))}
          </Grid2>

          {subscriptionPrices > 0 && (
            <PriceText color="primary">
              {t("Total")}: ${Number(subscriptionPrices).toFixed(2)}
            </PriceText>
          )}

          {/* Payment Method Selection for Subscriptions */}
          {selectedSubscriptionPlanId && subscriptionPrices > 0 && (
            <Box 
              sx={{ margin: "20px 0", display: "flex", justifyContent: "center" }}
              ref={subscriptionButtonRef}
            >
              <Paper elevation={3} sx={{ padding: "16px", borderRadius: "16px" }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#4a707d" }}>
                  {t("Choose Payment Method")}
                </Typography>
                <PaymentMethodToggle
                  value={paymentMethod}
                  exclusive
                  onChange={handlePaymentMethodChange}
                  aria-label="payment method"
                  sx={{ width: "100%" }}
                >
                  <ToggleButton 
                    value="airwallex" 
                    aria-label="credit card"
                    sx={{ minWidth: "150px" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CreditCard />
                      <AccountBalanceWallet />
                      <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                        {t("Card/Wallet")}
                      </Typography>
                    </Box>
                  </ToggleButton>
                  <ToggleButton 
                    value="paypal" 
                    aria-label="paypal"
                    sx={{ minWidth: "150px" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FaPaypal size="20px" />
                      <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                        {t("PayPal")}
                      </Typography>
                    </Box>
                  </ToggleButton>
                </PaymentMethodToggle>
              </Paper>
            </Box>
          )}

          {/* Airwallex Subscription DropIn Payment Section */}
          {selectedSubscriptionPlanId && 
           subscriptionPrices > 0 && 
           paymentMethod === "airwallex" && (
            <Box 
              sx={{ mt: 4, p: 3, backgroundColor: "rgba(255,255,255,0.9)", borderRadius: 2 }} 
              ref={subscriptionButtonRef}
            >
              <Typography variant="h6" sx={{ mb: 2, color: "#4a707d" }}>
                {t("Complete your subscription with Card/Wallet")}
              </Typography>
              <SubscriptionDropIn
                planId={selectedSubscriptionPlanId}
                amount={subscriptionAmount.toString()}
                currency="USD"
                env={AIRWALLEX_ENV}
                onSuccess={handleSubscriptionSuccess}
                onError={handlePaymentError}
                language={language}
                userData={userData}
                webSocketMessage={subscriptionMessage}
              />
            </Box>
          )}

          {/* PayPal Subscription Payment Section */}
          {selectedSubscriptionPlanId && 
           subscriptionPrices > 0 && 
           paymentMethod === "paypal" && (
            <Box
              className="paypal-subscription-container"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: "400px",
                margin: "0 auto",
                mt: 4,
                p: 3,
                backgroundColor: "rgba(255,255,255,0.9)",
                borderRadius: 2,
              }}
              ref={subscriptionButtonRef}
            >
              <Typography variant="h6" sx={{ mb: 2, color: "#4a707d" }}>
                {t("Complete your subscription with PayPal")}
              </Typography>
              <PayPalPurchase
                price={subscriptionPrices}
                planId={selectedSubscriptionPlanId}
                isSubscription={true}
                language={language}
              />
            </Box>
          )}

          {/* Legacy Braintree Payment Section for Subscription (if needed) */}
          {clientToken &&
            selectedSubscriptionPlanId &&
            subscriptionPrices > 0 &&
            paymentMethod !== "paypal" &&
            paymentMethod !== "airwallex" && (
              <Box
                className="braintree-dropin-container"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "400px",
                  margin: "0 auto",
                }}
              >
                <Box
                  sx={{ marginTop: "20px", textAlign: "center" }}
                  ref={subscriptionButtonRef}
                >
                  <PayPalPurchase
                    price={subscriptionPrices}
                    planId={selectedSubscriptionPlanId}
                    isSubscription={true}
                    language={language}
                  />
                </Box>
              </Box>
            )}
        </Container>
      </Box>

      {/* Coin Packages Section */}
      <Box
        ref={coinPackagesRef}
        sx={{
          backgroundColor: gridBackGraundPackage,
          padding: "8px",
          marginTop: "20px",
          position: "relative",
          backgroundImage: GENERAL.BACKGROUND,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            marginTop: "10px",
            fontWeight: "bold",
            color: "white",
            marginBottom: "20px",
          }}
        >
          {t("Select your coin package")}
        </Typography>
        <BuyCoinsSlider
          coinPackages={coinPackages}
          handlePackageSelection={handlePackageSelection}
        />
        {packagePrice > 0 && (
          <PriceText color="primary">
            {t("Total")}: ${Number(packagePrice).toFixed(2)}
          </PriceText>
        )}

        {/* Payment Method Selection for Packages */}
        {selectedPackagePlanId && packagePrice > 0 && (
          <Box 
            sx={{ margin: "20px 0", display: "flex", justifyContent: "center" }}
            ref={paymentSectionRef}
          >
            <Paper elevation={3} sx={{ padding: "16px", borderRadius: "16px" }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#4a707d" }}>
                {t("Choose Payment Method")}
              </Typography>
              <PaymentMethodToggle
                value={paymentMethod}
                exclusive
                onChange={handlePaymentMethodChange}
                aria-label="payment method"
                sx={{ width: "100%" }}
              >
                <ToggleButton 
                  value="airwallex" 
                  aria-label="credit card"
                  sx={{ minWidth: "150px" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CreditCard />
                    <AccountBalanceWallet />
                    <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                      {t("Card/Wallet")}
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton 
                  value="paypal" 
                  aria-label="paypal"
                  sx={{ minWidth: "150px" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FaPaypal size="20px" />
                    <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                      {t("PayPal")}
                    </Typography>
                  </Box>
                </ToggleButton>
              </PaymentMethodToggle>
            </Paper>
          </Box>
        )}

        {/* Airwallex Package DropIn Payment Section */}
        {selectedPackagePlanId && 
         packagePrice > 0 && 
         paymentMethod === "airwallex" && (
          <Box 
            sx={{ mt: 4, p: 3, backgroundColor: "rgba(255,255,255,0.9)", borderRadius: 2 }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "#4a707d" }}>
              {t("Complete your purchase with Card/Wallet")}
            </Typography>
            <DropIn
              amount={packageAmount.toString()}
              currency="USD"
              env={AIRWALLEX_ENV}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              language={language}
              webSocketMessage={packageMessage}
            />
          </Box>
        )}

        {/* PayPal Package Payment Section */}
        {selectedPackagePlanId && 
         packagePrice > 0 && 
         paymentMethod === "paypal" && (
          <Box
            className="paypal-package-container"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto",
              mt: 4,
              p: 3,
              backgroundColor: "rgba(255,255,255,0.9)",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "#4a707d" }}>
              {t("Complete your purchase with PayPal")}
            </Typography>
            <PayPalPurchase
              price={packagePrice}
              planId={selectedPackagePlanId}
              isSubscription={false}
              language={language}
            />
          </Box>
        )}

        {/* Legacy Braintree Payment Section for Packages (if needed) */}
        {clientToken && 
         selectedPackagePlanId && 
         packagePrice > 0 && 
         paymentMethod !== "paypal" && 
         paymentMethod !== "airwallex" && (
          <Box
            className="braintree-dropin-container"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            <Box sx={{ marginTop: "20px", textAlign: "center" }}>
              <PayPalPurchase
                price={packagePrice}
                planId={selectedPackagePlanId}
                isSubscription={false}
                language={language}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Support Section */}
      <Box
        sx={{
          backgroundColor: gridBackGraundPackage,
          p: 2,
          mt: 3,
          position: "relative",
          backgroundImage: GENERAL.BACKGROUND,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mt: 1.5,
            fontSize: "1.0rem",
            color: "white",
            mb: 2.5,
          }}
        >
          {t("buy_coffe")}
        </Typography>
        <SupportButton />
      </Box>
    </Box>
  );
};

export default PurchaseCoinsAirWallex;